import * as React from "react";
import { Dot, type DotTone } from "@/components/ui/dot";
import { getReliabilityFriendlyLabel } from "@/lib/display-labels";
import type { LineSummary } from "@/types/lines.types";

type Reliability = LineSummary["reliability"];

const RELIABILITY_TONE: Record<Reliability, DotTone> = {
  HIGH: "success",
  MEDIUM: "warning",
  LOW: "danger",
};

const RELIABILITY_FOOT: Record<Reliability, string> = {
  HIGH: "dentro do esperado",
  MEDIUM: "atenção parcial",
  LOW: "requer atenção",
};

const STAT_CARD = "bg-bg-surface px-3.5 py-3";
const STAT_LABEL = "text-[10px] font-medium uppercase tracking-[0.06em] text-text-secondary mb-1";
const STAT_VALUE = "font-mono text-[18px] font-medium leading-none text-text-primary";
const STAT_FOOT = "mt-1 flex items-center gap-1.5 text-[11px] text-text-tertiary";

interface Props {
  line: LineSummary;
}

export function LineMachinesSummaryStats({ line }: Props): React.ReactElement {
  const inactive = (line.influentStopped ?? 0) + (line.influentNodata ?? 0);
  const active = Math.max(0, line.machineCount - inactive);

  const reliability = line.reliability;
  const relTone = RELIABILITY_TONE[reliability];
  const relFoot = RELIABILITY_FOOT[reliability];

  const availabilityPct =
    line.availabilityEstimate7d == null ? null : Math.round(line.availabilityEstimate7d * 100);

  return (
    <div className="mt-3 grid grid-cols-3 gap-px overflow-hidden rounded-[10px] border border-border-subtle bg-border-subtle">
      {}
      <div className={STAT_CARD}>
        <p className={STAT_LABEL}>Máquinas</p>
        <p className={STAT_VALUE}>{line.machineCount}</p>
        <p className={STAT_FOOT}>
          <Dot tone={active > 0 ? "success" : "neutral"} size={7} />
          {active} {active === 1 ? "ativa" : "ativas"}
        </p>
      </div>

      {}
      <div className={STAT_CARD}>
        <p className={STAT_LABEL}>Disp 7d (est.)</p>
        <p className={STAT_VALUE}>{availabilityPct == null ? "—" : `${availabilityPct}%`}</p>
        {availabilityPct != null ? <p className={STAT_FOOT}>baseado nos últimos 7 dias</p> : null}
      </div>

      {}
      <div className={STAT_CARD}>
        <p className={STAT_LABEL}>Confiabilidade</p>
        <p className="text-[18px] font-medium leading-none text-text-primary">
          {getReliabilityFriendlyLabel(reliability)}
        </p>
        <p className={STAT_FOOT}>
          <Dot tone={relTone} size={7} />
          {relFoot}
        </p>
      </div>
    </div>
  );
}
